import { useMemo, type RefObject } from "react";
import type { MapRef } from "../components/MapCanvas";
import type { MapSpace } from "../types";
import { coordKey } from "../utils/coords";

export interface SpaceCluster<S extends MapSpace = MapSpace> {
  key: string;
  members: S[];
  lng: number;
  lat: number;
  isSameLocation: boolean;
}

export type ClusterAction =
  | { kind: "select"; id: string }
  | { kind: "zoomIn"; lng: number; lat: number; zoom: number }
  | { kind: "sameLocation"; ids: string[] };

export function clusterSpacesByPixelDistance<S extends MapSpace>(
  spaces: S[],
  project: ((lngLat: [number, number]) => { x: number; y: number }) | null,
  thresholdPx: number,
): SpaceCluster<S>[] {
  if (!project) {
    const byCoord = new Map<string, S[]>();
    spaces.forEach((s) => {
      const key = coordKey(s.lat, s.lng);
      if (!byCoord.has(key)) byCoord.set(key, []);
      byCoord.get(key)!.push(s);
    });
    return Array.from(byCoord.entries()).map(([key, members]) => ({
      key,
      members,
      lng: members[0].lng,
      lat: members[0].lat,
      isSameLocation: true,
    }));
  }

  const points = spaces.map((s) => ({ space: s, px: project([s.lng, s.lat]) }));
  const used = new Array(points.length).fill(false);
  const clusters: SpaceCluster<S>[] = [];

  for (let i = 0; i < points.length; i++) {
    if (used[i]) continue;
    used[i] = true;
    const indices = [i];
    for (let j = i + 1; j < points.length; j++) {
      if (used[j]) continue;
      const dx = points[i].px.x - points[j].px.x;
      const dy = points[i].px.y - points[j].px.y;
      if (Math.sqrt(dx * dx + dy * dy) <= thresholdPx) {
        used[j] = true;
        indices.push(j);
      }
    }

    const members = indices.map((idx) => points[idx].space);
    const isSameLocation = members.every(
      (m) =>
        coordKey(m.lat, m.lng) === coordKey(members[0].lat, members[0].lng),
    );
    const signature = members
      .map((m) => m.id)
      .sort()
      .join(",");

    clusters.push({
      key: signature,
      members,
      lng: members.reduce((sum, m) => sum + m.lng, 0) / members.length,
      lat: members.reduce((sum, m) => sum + m.lat, 0) / members.length,
      isSameLocation,
    });
  }

  return clusters;
}

interface UseSpaceClustersOptions {
  mapRef: RefObject<MapRef | null>;
  viewZoom: number;
  byPixelDistance?: boolean;
  thresholdPx?: number;
}

export function useSpaceClusters<S extends MapSpace>(
  spaces: S[],
  {
    mapRef,
    viewZoom,
    byPixelDistance = false,
    thresholdPx = 50,
  }: UseSpaceClustersOptions,
): {
  clusters: SpaceCluster<S>[];
  resolveClusterClick: (cluster: SpaceCluster<S>) => ClusterAction;
} {
  const exactClusters = useMemo(
    () => clusterSpacesByPixelDistance(spaces, null, thresholdPx),
    [spaces, thresholdPx],
  );
  const map = mapRef.current;
  const clusters =
    byPixelDistance && map
      ? clusterSpacesByPixelDistance(
          spaces,
          (lngLat) => map.project(lngLat),
          thresholdPx,
        )
      : exactClusters;

  const resolveClusterClick = (cluster: SpaceCluster<S>): ClusterAction => {
    if (cluster.members.length === 1) {
      return { kind: "select", id: cluster.members[0].id };
    }
    if (!cluster.isSameLocation) {
      return {
        kind: "zoomIn",
        lng: cluster.lng,
        lat: cluster.lat,
        zoom: Math.min(viewZoom + 3, 20),
      };
    }
    // Identical coordinates can never be split apart visually — let the
    // caller highlight every member and pick which card to scroll to.
    return { kind: "sameLocation", ids: cluster.members.map((m) => m.id) };
  };

  return { clusters, resolveClusterClick };
}
